import { CardHightlight, DialogReceipt, TableRecipes } from "@components";
import DialogObservation from "@components/Dialog/Observation";
import { Vehicle } from "@components/Table/Recipes/Table";
import { Dialog, Notification } from "@rio-cloud/rio-uikit";
import {
  createTransaction,
  deleteFile,
  deleteTransaction,
  editTransaction,
  exportTransactions,
  getDetailTransaction,
  getDRESummary,
  getFile,
  getTableData,
} from "@services";
import { useAppDispatch } from "@store";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { ITransactionPayload } from "@types";
import { formatValue, parseCurrencyBRL } from "@utils";
import { formatDateRange } from "@utils/dateRange";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormState } from "src/components/Dialog/Receipt/form";

interface ITableData {
  driverIds: string[];
  id: string;
  transactionDate: string;
  amount: number;
  branchName: string;
  segmentBussinessName: string;
  clientName: string;
  vehiclePlate: string;
  driverName: string;
  categoryName: string;
  subcategoryName: string;
  taxPercentage: string;
}

const RecipesScreen = () => {
  const dispatch = useAppDispatch();
  const PAGE_SIZE = 10;
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();

  // Auto-open modal if add=receipt query param is present
  useEffect(() => {
    if (searchParams.get("add") === "receipt") {
      setOpenModal(true);
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecipeRecords, setTotalRecipeRecords] = useState(0);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalRecipeAmount, setTotalRecipeAmount] = useState(0);
  const [tableData, setTableData] = useState<Vehicle[]>([]);
  const [selected, setSelected] = useState<FormState & { id: string }>();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmFileId, setDeleteConfirmFileId] = useState<string | null>(null);
  const [observation, setObservation] = useState({
    open: false,
    value: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");

  const handleSave = async (data: FormState, callback?: () => void, dialogModal = true) => {
    const payload: ITransactionPayload = {
      amount: parseCurrencyBRL(data?.value),
      branchId: Number(data?.branch),
      categoryId: Number(data?.category),
      description: data?.observation,
      driverId: data?.driver.length > 0 ? data?.driver : null,
      vehicleId: data?.vehicle ? data?.vehicle : null,
      segmentBussinessId: Number(data?.segment),
      subcategoryId: Number(data?.subcategory),
      supplierClientId: Number(data?.supplier),
      taxPercentage: Number(data?.taxes),
      transactionDate: data?.date,
      tripCode: data.trip,
      transactionType: "RECEITA",
    };

    const messages = {
      success: selected ? "Receita editada com sucesso." : "Receita criada com sucesso.",
      error: selected ? "Não foi possível editar a receita." : "Não foi possível criar a receita.",
    };

    try {
      selected
        ? await editTransaction(selected.id, payload, file || undefined)
        : await createTransaction(payload, file || undefined);

      if (dialogModal) {
        setOpenModal(false);
      }
      Notification.success(messages.success);
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error(messages.error);
    }
  };

  const toVehicleRow = (item: ITableData): Vehicle => ({
    id: item.id,
    date: moment(item.transactionDate).format("DD/MM/YYYY"),
    amount: item.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
    branch: item.branchName,
    segment: item.segmentBussinessName,
    customer: item.clientName,
    vehicle: item.vehiclePlate,
    driver: item.driverName,
    driverIds: item.driverIds,
    category: item.categoryName,
    subcategory: item.subcategoryName,
    taxes: `${item.taxPercentage}%`,
  });

  const deriveTotalRecords = (payload: any, fallback: number) => {
    const candidates = [
      payload?.totalElements,
      payload?.total,
      payload?.totalRecords,
      payload?.totalItems,
      payload?.totalReceitas,
      payload?.totalDespesas,
      payload?.meta?.totalElements,
      payload?.meta?.total,
      payload?.data?.totalElements,
      payload?.data?.total,
    ];

    const found = candidates.find((value) => typeof value === "number");

    return typeof found === "number" ? found : fallback;
  };

  const fetchTableData = useCallback(
    async (page = 0, append = false) => {
      const setLoading = append ? setIsLoadingMore : setIsLoadingTable;
      setLoading(true);

      try {
        const response = await getTableData(startDate, endDate, "RECEITA", {
          page,
          size: PAGE_SIZE,
        });

        const normalizedPayload = response?.data ?? response;
        const content = Array.isArray(normalizedPayload?.content) ? normalizedPayload.content : [];
        const formattedRows = content.map(toVehicleRow);

        setTableData((prev) => (append ? [...prev, ...formattedRows] : formattedRows));
        setTotalRecipeRecords(deriveTotalRecords(normalizedPayload, formattedRows.length));
        setCurrentPage(page);
      } catch (error) {
        Notification.error("Não foi possível carregar as receitas.");
      } finally {
        setLoading(false);
      }
    },
    [endDate, startDate]
  );

  const getDashboard = useCallback(async () => {
    const response = await getDRESummary(startDate, endDate);
    setTotalRecipeAmount(response.data.totalReceitas || 0);
  }, [endDate, startDate]);

  const fetchInitial = useCallback(async () => {
    await Promise.all([getDashboard(), fetchTableData(0)]);
  }, [fetchTableData, getDashboard]);

  const handleLoadMore = () => {
    if (isLoadingMore || tableData.length >= totalRecipeRecords) {
      return;
    }

    fetchTableData(currentPage + 1, true);
  };

  const handleDeleteRecipe = async (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteFileRecipe = async (id: string) => {
    setDeleteConfirmFileId(id);
  };

  const confirmDeleteFileRecipe = async () => {
    if (!deleteConfirmFileId) return;

    try {
      await deleteFile(deleteConfirmFileId);
      setDeleteConfirmFileId(null);
      Notification.success("Arquivo excluído com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir o arquivo.");
    }
  };

  const confirmDeleteRecipe = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteTransaction(deleteConfirmId);
      setDeleteConfirmId(null);
      Notification.success("Receita excluída com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir a receita.");
    }
  };

  const getDownloadFile = async (transactionId: string) => {
    try {
      const response = await getFile(transactionId);

      window.open(response.data.downloadUrl, "_blank");
      Notification.success("Comprovante baixado com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível baixar o comprovante.");
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);

      if (response?.data?.fileNameAttachmentUrl) {
        setFileName(response?.data?.fileNameAttachmentUrl);
      }

      const mount: FormState & { id: string } = {
        branch: response?.data?.branchId?.toString(),
        category: response?.data?.categoryId?.toString(),
        date: response?.data?.transactionDate,
        segment: response?.data?.segmentBussinessId?.toString(),
        driver: response?.data?.driverIds || (response?.data?.driverId ? [response?.data?.driverId] : []),
        subcategory: response?.data?.subcategoryId?.toString(),
        observation: response?.data?.description,
        supplier: response?.data?.supplierClientId?.toString(),
        taxes: response?.data?.taxPercentage,
        trip: response?.data?.tripCode,
        value: formatValue(response?.data?.amount, false),
        vehicle: response?.data?.vehicleId?.toString(),
        id: response.data.id,
      };
      setSelected(mount);
      setOpenModal(true);
    } catch (error) {
      Notification.error("Não foi possível obter os detalhes da receita.");
    }
  };

  const handleObservationDefault = () => {
    setObservation({
      open: false,
      value: "",
    });
  };

  const handleDownload = async () => {
    try {
      await exportTransactions(startDate, endDate, "RECEITA");
      Notification.success("Receitas exportadas com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível exportar as receitas.");
    }
  };

  const handleObservation = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      setObservation({
        open: true,
        value: response?.data?.description,
      });
    } catch (error) {
      Notification.error("Não foi possível obter os detalhes da receita.");
      handleObservationDefault();
    }
  };

  const handleOnCLose = () => {
    setOpenModal(false);
    setSelected(undefined);
  };

  useEffect(() => {
    const loadData = async () => {
      setCurrentPage(0);
      setTableData([]);
      setTotalRecipeRecords(0);
      await fetchInitial();
    };

    loadData();
  }, [startDate, endDate, fetchInitial]);

  return (
    <div className="padding-20 display-flex flex-column gap-20 height-100pct">
      <div className="display-flex width-100pct gap-50">
        <div className="width-50pct">
          <CardHightlight
            icon="rioglyph rioglyph-calendar"
            label="Período"
            value={formatDateRange(new Date(startDate + "T00:00:00"), new Date(endDate + "T00:00:00"))}
            direction="LEFT"
          />
        </div>
        <div className="width-50pct">
          <CardHightlight
            icon="rioglyph rioglyph-arrow-up text-color-rating-4"
            label="Valor total das receitas"
            value={totalRecipeAmount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            direction="RIGHT"
          />
        </div>
      </div>
      <div className="flex-1-1 height-100pct">
        <TableRecipes
          data={tableData}
          totalData={totalRecipeRecords}
          startDate={startDate}
          endDate={endDate}
          handleStartDate={(date) => dispatch(setStartDate(date))}
          handleEndDate={(date) => dispatch(setEndDate(date))}
          viewType="TABLE"
          onLoadMore={handleLoadMore}
          handleRefresh={fetchInitial}
          handleDownload={handleDownload}
          handleCreate={() => {
            setFileName("");
            setOpenModal(true);
          }}
          handleDelete={handleDeleteRecipe}
          onDeleteFile={handleDeleteFileRecipe}
          onGetFile={getDownloadFile}
          handleEdit={handleEdit}
          handleObservation={handleObservation}
          isLoadingMore={isLoadingMore}
          isLoadingTable={isLoadingTable}
        />
      </div>
      <DialogReceipt
        title={selected ? "Editar receita" : "Nova receita"}
        show={openModal}
        onClose={handleOnCLose}
        onSave={handleSave}
        selected={selected}
        handleFile={(file) => setFile(file)}
        handleClearFile={() => setFile(null)}
        fileName={fileName}
        changeFileName={(filename) => setFileName(filename)}
      />
      <DialogObservation
        onClose={handleObservationDefault}
        show={observation.open}
        title={"Observações"}
        value={observation.value}
      />

      <Dialog
        show={deleteConfirmId !== null}
        title={<span className="text-medium text-size-16">Excluir receita</span>}
        body={
          <div className="padding-20">
            <p>Tem certeza que deseja excluir esta receita?</p>
            <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
          </div>
        }
        footer={
          <div className="display-flex gap-10 justify-content-end">
            <button className="btn btn-default" onClick={() => setDeleteConfirmId(null)}>
              Cancelar
            </button>
            <button className="btn btn-danger" onClick={confirmDeleteRecipe}>
              Excluir
            </button>
          </div>
        }
        onClose={() => setDeleteConfirmId(null)}
        className="exampleDialog"
        showCloseButton
        bsSize="sm"
      />

      <Dialog
        show={deleteConfirmFileId !== null}
        title={<span className="text-medium text-size-16">Excluir comprovante</span>}
        body={
          <div className="padding-20">
            <p>Tem certeza que deseja excluir este comprovante?</p>
            <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
          </div>
        }
        footer={
          <div className="display-flex gap-10 justify-content-end">
            <button className="btn btn-default" onClick={() => setDeleteConfirmFileId(null)}>
              Cancelar
            </button>
            <button className="btn btn-danger" onClick={confirmDeleteFileRecipe}>
              Excluir
            </button>
          </div>
        }
        onClose={() => setDeleteConfirmFileId(null)}
        className="exampleDialog"
        showCloseButton
        bsSize="sm"
      />
    </div>
  );
};

export default RecipesScreen;
